// Exchange Rate Service
// Fetches and caches exchange rates from the backend API

const CACHE_KEY = 'exchange_rates_cache';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour in milliseconds

// Get cached rates from localStorage
const getCachedRates = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    
    const { rates, timestamp } = JSON.parse(cached);
    const age = Date.now() - timestamp;
    
    if (age > CACHE_TTL_MS) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    
    return rates;
  } catch {
    return null;
  }
};

// Save rates to localStorage
const setCachedRates = (rates) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      rates,
      timestamp: Date.now()
    }));
  } catch {
    // Ignore storage errors
  }
};

/**
 * Fetch exchange rates from the backend
 * @param {string} baseCurrency - Base currency code (e.g., 'USD')
 * @param {function} authFetch - Authenticated fetch function from AuthContext
 * @returns {Promise<Object>} Exchange rates object
 */
export const fetchExchangeRates = async (baseCurrency, authFetch) => {
  // Check cache first
  const cached = getCachedRates();
  if (cached && cached[baseCurrency]) {
    return cached;
  }
  
  try {
    const response = await authFetch(`/api/exchange-rates/all`);
    if (response.ok) {
      const data = await response.json();
      setCachedRates(data.rates);
      return data.rates;
    }
  } catch (error) {
    console.error('Failed to fetch exchange rates:', error);
  }
  
  // Return cached data even if stale, or fallback rates
  return cached || getFallbackRates();
};

/**
 * Convert an amount from one currency to another
 * @param {number} amount - Amount to convert
 * @param {string} fromCurrency - Source currency code
 * @param {string} toCurrency - Target currency code
 * @param {Object} rates - Exchange rates object (from fetchExchangeRates)
 * @returns {number} Converted amount
 */
export const convertAmount = (amount, fromCurrency, toCurrency, rates) => {
  if (!amount || fromCurrency === toCurrency) {
    return amount;
  }
  
  if (!rates || !rates[fromCurrency] || !rates[fromCurrency][toCurrency]) {
    // Fallback: return original amount if rates not available
    console.warn(`No rate found for ${fromCurrency} -> ${toCurrency}`);
    return amount;
  }
  
  const rate = rates[fromCurrency][toCurrency];
  return Math.round(amount * rate * 100) / 100; // Round to 2 decimal places
};

/**
 * Get fallback exchange rates (approximate values)
 * Used when API is unavailable
 */
const getFallbackRates = () => ({
  'CAD': {
    'CAD': 1, 'USD': 0.73, 'EUR': 0.62, 'GBP': 0.54, 
    'INR': 66, 'JPY': 115, 'CNY': 5.07, 'AUD': 1.05
  },
  'USD': {
    'USD': 1, 'CAD': 1.37, 'EUR': 0.85, 'GBP': 0.74,
    'INR': 90.4, 'JPY': 157, 'CNY': 6.95, 'AUD': 1.44
  },
  'EUR': {
    'EUR': 1, 'USD': 1.18, 'CAD': 1.61, 'GBP': 0.87,
    'INR': 106.5, 'JPY': 185, 'CNY': 8.18, 'AUD': 1.69
  },
  'GBP': {
    'GBP': 1, 'USD': 1.35, 'EUR': 1.15, 'CAD': 1.85,
    'INR': 122, 'JPY': 212, 'CNY': 9.4, 'AUD': 1.94
  },
  'INR': {
    'INR': 1, 'USD': 0.011, 'EUR': 0.0094, 'GBP': 0.0082,
    'CAD': 0.015, 'JPY': 1.74, 'CNY': 0.077, 'AUD': 0.016
  },
  'JPY': {
    'JPY': 1, 'USD': 0.0064, 'EUR': 0.0054, 'GBP': 0.0047,
    'CAD': 0.0087, 'INR': 0.58, 'CNY': 0.044, 'AUD': 0.0092
  },
  'CNY': {
    'CNY': 1, 'USD': 0.144, 'EUR': 0.122, 'GBP': 0.106,
    'CAD': 0.197, 'INR': 13, 'JPY': 22.6, 'AUD': 0.207
  },
  'AUD': {
    'AUD': 1, 'USD': 0.695, 'EUR': 0.59, 'GBP': 0.515,
    'CAD': 0.95, 'INR': 62.8, 'JPY': 109, 'CNY': 4.83
  }
});

export const SUPPORTED_CURRENCIES = ['USD', 'EUR', 'GBP', 'INR', 'JPY', 'CNY', 'AUD', 'CAD'];
